import React, { useEffect } from 'react';
import { Form, Radio } from 'antd';
import { useState } from 'react';
import {
	RBHBox,
	RBHButton,
	RBHRadio,
	RBHText,
	RBHTextInput,
} from '../../../../../shared/components';
import {
	ExclamationCircleFilled,
	PhoneOutlined,
	UserOutlined,
} from '@ant-design/icons';
import {
	INVITATION_STATE_KEY,
	InvitationFormData,
	invitationStateSelector,
	InvitationStep,
} from '../redux/invitation-reducer';
import { useDispatch, useSelector } from 'react-redux';
import { invitationCreators } from '../redux/invitation-action';
import styles from './styles';
import './styles.css';

const MORNING_TIME = '09:00 - 12:00';
const AFTERNOON_TIME = '13:00 - 18:00';
export const DEFAULT_AVAILABLE_TIME = MORNING_TIME;
const timeOptions = [MORNING_TIME, AFTERNOON_TIME];

const isValidPhoneNumber = (phone: string) => /^0\d{8,9}$/.test(phone);

export const InvitationFormSection = () => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const invitationFormData = useSelector(
		invitationStateSelector(INVITATION_STATE_KEY.FORM),
	) as InvitationFormData;
	const [selectedTime, setSelectedTime] = useState(DEFAULT_AVAILABLE_TIME);
	const [formInvalid, setFormInvalid] = useState(true);
	const [editedFields, setEditedFields] = useState({
		contactName: false,
		contactNumber: false,
	});
	const [focusedField, setFocusedField] = useState<string | null>(null);
	const invitationStep = useSelector(
		invitationStateSelector(INVITATION_STATE_KEY.STEP),
	  ) as InvitationStep;

	const [inputValues, setInputValues] = useState({
		contactName: '',
		contactNumber: '',
	});
	const hasNameError =
		editedFields.contactName && !inputValues.contactName.trim();
	const shouldFloatName =
		focusedField === 'contactName' || !!inputValues.contactName;

	const shouldFloatPhone =
		focusedField === 'contactNumber' || !!inputValues.contactNumber;
	const hasPhoneError =
		editedFields.contactNumber &&
		(!inputValues.contactNumber.trim() ||
			!isValidPhoneNumber(inputValues.contactNumber));

	const updateSubmitState = (nextValues?: any) => {
		const values = nextValues || form.getFieldsValue();
		const hasContactName = !!values.contactName?.trim();
		const isPhoneValid = isValidPhoneNumber(values.contactNumber || '');
		const hasAvailableTime = !!values.availableTime;

		setFormInvalid(!(hasContactName && isPhoneValid && hasAvailableTime));
	};

	const handleValuesChange = (changedValues: any, allValues: any) => {
		setEditedFields((prevState) => ({
			...prevState,
			...(Object.prototype.hasOwnProperty.call(changedValues, 'contactName')
				? { contactName: true }
				: {}),
			...(Object.prototype.hasOwnProperty.call(changedValues, 'contactNumber')
				? { contactNumber: true }
				: {}),
		}));
		setInputValues({
			contactName: allValues.contactName || '',
			contactNumber: allValues.contactNumber || '',
		});
		updateSubmitState(allValues);
	};

	const handlePhoneKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const allowedControlKeys = [
			'Backspace',
			'Delete',
			'Tab',
			'ArrowLeft',
			'ArrowRight',
			'Home',
			'End',
		];

		if (
			allowedControlKeys.includes(event.key) ||
			event.ctrlKey ||
			event.metaKey
		) {
			return;
		}

		if (!/^\d$/.test(event.key)) {
			event.preventDefault();
		}
	};

	const handleTimeChange = (event: any) => {
		const value = event?.target?.value;
		setSelectedTime(value);
		form.setFieldsValue({
			availableTime: value,
		});
		updateSubmitState({
			...form.getFieldsValue(),
			availableTime: value,
		});
	};

	const handlePhonePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		const pastedText = event.clipboardData.getData('text');

		if (!/^\d+$/.test(pastedText)) {
			event.preventDefault();
		}
	};

	const handleFormSubmit = () => {
		const formValues = form.getFieldsValue();
		const values: InvitationFormData = {
			contactName: formValues.contactName || '',
			contactNumber: formValues.contactNumber || '',
			availableTime: selectedTime,
			token: invitationFormData.token || '',
			campaignName: invitationFormData.campaignName,
			channel: invitationFormData.channel,
		};

		dispatch(
			invitationCreators.setInvitationState(INVITATION_STATE_KEY.FORM, values),
		);
		dispatch(
			invitationCreators.setInvitationState(INVITATION_STATE_KEY.STEP, 'terms'),
		);
	};

	useEffect(() => {
		form.setFieldsValue({
			contactName: invitationFormData.contactName,
			contactNumber: invitationFormData.contactNumber,
			availableTime: invitationFormData.availableTime,
		});
		setSelectedTime(invitationFormData.availableTime);
		setInputValues({
			contactName: invitationFormData.contactName || '',
			contactNumber: invitationFormData.contactNumber || '',
		});
		updateSubmitState(invitationFormData);
	}, [form, invitationFormData]);

	if (invitationStep !== 'form') {
		return null;
	}

	return (
		<Form
			className='invitation-form'
			form={form}
			layout='vertical'
			initialValues={{ availableTime: selectedTime }}
			onValuesChange={handleValuesChange}
			onFinish={handleFormSubmit}
			style={styles.form}
		>
			<RBHBox className='invitation-form-section' style={styles.formSection}>
				<RBHBox>
					<RBHText
						data-testid='invitation-section-personal'
						id='invitation-section-personal'
						style={styles.sectionTitle}
					>
						ข้อมูลของคุณ
					</RBHText>
					<RBHBox className='invitation-input-grid' style={styles.inputGrid}>
						<Form.Item
							name='contactName'
							rules={[
								{
									required: true,
									message: (
										<span style={styles.inputRule}>
											<ExclamationCircleFilled
												style={styles.exclamationIcon}
												translate={null}
											/>
											โปรดระบุชื่อ - นามสกุล
										</span>
									),
								},
							]}
							style={styles.formItem}
						>
							<RBHBox
								className='invitation-input-wrap'
								style={styles.inputWrap}
							>
								<span
									style={
										shouldFloatName
											? styles.floatingLabel
											: styles.customPlaceholder
									}
								>
									ชื่อ - นามสกุล*
								</span>

								<RBHTextInput
									data-testid='invitation-fullname'
									id='invitation-fullname'
									prefix={
										<UserOutlined style={styles.inputPrefix} translate={null} />
									}
									style={{
										...styles.input,
										...(hasNameError ? styles.inputError : {}),
									}}
									onFocus={() => setFocusedField('contactName')}
									onBlur={() => setFocusedField(null)}
								/>
							</RBHBox>
						</Form.Item>

						<Form.Item
							name='contactNumber'
							getValueFromEvent={(event) =>
								event?.target?.value?.replace(/\D/g, '')
							}
							rules={[
								{
									required: true,
									message: (
										<span style={styles.inputRule}>
											<ExclamationCircleFilled
												style={styles.exclamationIcon}
												translate={null}
											/>
											โปรดระบุเบอร์ติดต่อกลับ
										</span>
									),
								},
								{
									validator: (_, value) => {
										if (!value || isValidPhoneNumber(value)) {
											return Promise.resolve();
										}
										return Promise.reject(
											<span style={styles.inputRule}>
												<ExclamationCircleFilled
													style={styles.exclamationIcon}
													translate={null}
												/>
												เบอร์ติดต่อต้องขึ้นต้นด้วย 0 และมี 9-10 หลัก
											</span>,
										);
									},
								},
							]}
							style={styles.formItem}
						>
							<RBHBox
								className='invitation-input-wrap'
								style={styles.inputWrap}
							>
								<span
									style={
										shouldFloatPhone
											? styles.floatingLabel
											: styles.customPlaceholder
									}
								>
									เบอร์ติดต่อกลับ*
								</span>
								<RBHTextInput
									data-testid='invitation-phone'
									id='invitation-phone'
									maxLength={10}
									inputMode='numeric'
									pattern='[0-9]*'
									prefix={
										<PhoneOutlined
											style={styles.inputPrefix}
											translate={null}
										/>
									}
									style={{
										...styles.input,
										...(hasPhoneError ? styles.inputError : {}),
									}}
									onKeyDown={handlePhoneKeyDown}
									onPaste={handlePhonePaste}
									onFocus={() => setFocusedField('contactNumber')}
									onBlur={() => setFocusedField(null)}
								/>
							</RBHBox>
						</Form.Item>
					</RBHBox>
				</RBHBox>

				<RBHBox>
					<RBHText
						data-testid='invitation-section-callback'
						id='invitation-section-callback'
						style={styles.sectionTitle}
					>
						ช่วงเวลาที่สะดวกให้ติดต่อกลับ
					</RBHText>
					<RBHText
						data-testid='invitation-section-subtitle'
						id='invitation-section-subtitle'
						style={styles.sectionSubtitle}
					>
						โรบินฮู้ดจะติดต่อกลับในวันทำการ (จันทร์ - ศุกร์)
						ยกเว้นวันหยุดนักขัตฤกษ์
					</RBHText>

					<Form.Item
						name='availableTime'
						rules={[{ required: true, message: 'กรุณาเลือกช่วงเวลา' }]}
						style={styles.formItem}
					>
						<Radio.Group
							className='invitation-radio-group'
							value={selectedTime}
							onChange={handleTimeChange}
							style={{ width: '100%' }}
						>
							<RBHBox className='invitation-time-grid' style={styles.timeGrid}>
								{timeOptions.map((timeOption) => (
									<RBHBox
										className='invitation-time-card'
										key={timeOption}
										style={{
											...styles.radioCard,
											...(selectedTime === timeOption
												? styles.radioCardActive
												: {}),
										}}
										onClick={() =>
											handleTimeChange({
												target: { value: timeOption },
											})
										}
									>
										<RBHRadio
											value={timeOption}
											data-testid={`invitation-time-${timeOption}`}
											id={`invitation-time-${timeOption}`}
											style={styles.radio}
										>
											{timeOption}
										</RBHRadio>
									</RBHBox>
								))}
							</RBHBox>
						</Radio.Group>
					</Form.Item>
				</RBHBox>

				<RBHBox className='invitation-button-wrap' style={styles.buttonWrap}>
					<RBHButton
						id='invitation-submit'
						data-testid='invitation-submit'
						htmlType='submit'
						disabled={formInvalid}
						style={{
							...styles.submitButton,
							...(formInvalid ? styles.submitButtonDisabled : {}),
						}}
					>
						ยืนยันการเข้าร่วม
					</RBHButton>
				</RBHBox>
			</RBHBox>
		</Form>
	);
};
